<template>
    <VfModal scrolls>
        <template #header>Parked calls · {{ lot.name }}</template>

        <p v-if="showRetrieve">
            Retrieve connects a parked call to your selected extension — the lot's retrieval policy decides whether it's allowed.
        </p>

        <FParkedCalls
            :lot="lot"
            :now="now"
            :retrievable="canRetrieve"
            :show-call-link="hasCallHistoryAccess"
            @snapshot="s => (snapshot = s)"
            @retrieve="retrieve"
            @view-call="callback()"
        />

        <template #footer>
            <button v-if="showRetrieve" type="button" class="zs-btn" @click="retrieveOldest()">Retrieve oldest</button>
            <button type="button" class="zs-btn" @click="callback()">Close</button>
        </template>
    </VfModal>
</template>

<script lang="ts" setup>
import { VfModal } from '@zyno-io/vue-foundation';
import { computed, onMounted, onUnmounted, ref } from 'vue';

import type { IParkingLotCallResponse, IParkingLotCallsResponse, IParkingLotWithOccupancyResponse } from '@/openapi-clients-generated/talk';

import { canListParkedCalls, oldestParkedCall } from '@/modules/talk/helpers/parking-occupancy';
import { retrieveParkedCall } from '@/modules/talk/shared/parking-retrieval';
import { useTalkHelpers } from '@/modules/talk/shared/store';
import { dialerAvailable } from '@/shared/services/dialer.service';

import FParkedCalls from './f-parked-calls.vue';

const props = defineProps<{
    lot: IParkingLotWithOccupancyResponse;
    callback: () => void;
}>();

const { hasCallHistoryAccess } = useTalkHelpers();

// Retrieval hands the call to the softphone dialer, so it is only offered where one is registered —
// and never for a legacy-backend lot, which has no parked-calls list to retrieve from.
const canRetrieve = computed(() => dialerAvailable.value && canListParkedCalls(props.lot));
// The table reports each snapshot as it lands; "Retrieve oldest" is that snapshot's longest-waiting
// row, retrieved exactly like the row's own button — so it only shows once there is such a row.
const snapshot = ref<IParkingLotCallsResponse>();
const oldest = computed(() => oldestParkedCall(snapshot.value));
const showRetrieve = computed(() => canRetrieve.value && !!oldest.value);

/** Hand the retrieval to the dialer and close: the call, not this list, is what the user wants next. */
function retrieve(item: IParkingLotCallResponse) {
    retrieveParkedCall(props.lot, item);
    props.callback();
}

function retrieveOldest() {
    if (oldest.value) retrieve(oldest.value);
}

const now = ref(Date.now());
let clockTimer: ReturnType<typeof setInterval> | undefined;

onMounted(() => {
    // Started synchronously: a modal closed the instant it opens must still find the timer to clear.
    clockTimer = setInterval(() => (now.value = Date.now()), 1000);
});

onUnmounted(() => {
    if (clockTimer) clearInterval(clockTimer);
});
</script>
